import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Skeleton, Space, Tag, Typography, message,
} from 'antd';
import type { StudentHomeworkRow } from '@velora/contracts';
import { ApiError, productApi } from '../../api';

/**
 * Student — My homework.
 * List of published homework for my section with per-item status + submit.
 * Missing-status items get a red border to hint action.
 */
const STATUS_TONE: Record<string, string> = {
  graded: 'success', submitted: 'processing', returned: 'warning', missing: 'error', pending: 'default',
};

export function StudentHomeworkPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [submitFor, setSubmitFor] = useState<StudentHomeworkRow | null>(null);
  const q = useQuery({
    queryKey: ['student-homework', institution],
    queryFn: () => productApi.getStudentHomework(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  const rows = q.data?.rows ?? [];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>My homework</Typography.Title>
      {rows.length === 0 ? (
        <Empty description="No homework assigned yet." style={{ padding: 20 }} />
      ) : (
        rows.map((r) => (
          <Card
            key={r.id}
            size="small"
            style={{ borderColor: r.my_status === 'missing' ? '#f4a3a0' : undefined }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <b style={{ fontSize: 15 }}>{r.title}</b>
              <Tag>{r.subject_code}</Tag>
              <div style={{ flex: 1 }} />
              <Tag color={STATUS_TONE[r.my_status] ?? 'default'}>{r.my_status}</Tag>
            </div>
            <div style={{ color: '#5b6478', fontSize: 12, marginTop: 4 }}>
              {r.subject_name}{r.teacher_name ? ` · ${r.teacher_name}` : ''} · due <b>{r.due_on}</b>
            </div>
            {r.description && <div style={{ marginTop: 8, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{r.description}</div>}
            {r.my_grade_out_of_10 != null && (
              <div style={{ marginTop: 8, fontSize: 13 }}>Grade: <b>{r.my_grade_out_of_10}/10</b></div>
            )}
            {r.my_feedback && <div style={{ marginTop: 4, fontSize: 12, color: '#553a9c' }}>Feedback: {r.my_feedback}</div>}
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
              {r.my_status === 'graded' ? null : (
                <Button size="small" type="primary" onClick={() => setSubmitFor(r)}>
                  {r.my_status === 'submitted' || r.my_status === 'returned' ? 'Re-submit' : 'Submit'}
                </Button>
              )}
            </div>
          </Card>
        ))
      )}

      <SubmitModal
        target={submitFor}
        institution={institution}
        onClose={() => setSubmitFor(null)}
        onDone={() => {
          setSubmitFor(null);
          void qc.invalidateQueries({ queryKey: ['student-homework', institution] });
          void message.success('Submitted');
        }}
      />
    </Space>
  );
}

function SubmitModal({
  target, institution, onClose, onDone,
}: { target: StudentHomeworkRow | null; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<{ note: string }>();
  const m = useMutation({
    mutationFn: (v: { note?: string }) => productApi.submitHomework(institution, target!.id, v),
    onSuccess: () => { form.resetFields(); onDone(); },
  });
  const body = m.error instanceof ApiError ? (m.error.body as { message?: string } | null) : null;
  const err = body?.message ?? (m.error instanceof Error ? m.error.message : null);
  return (
    <Modal
      open={!!target}
      title={target ? `Submit · ${target.title}` : ''}
      onCancel={() => { m.reset(); onClose(); }}
      okText="Submit"
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      {target && (
        <Form form={form} layout="vertical" initialValues={{ note: target.my_note ?? '' }}
          onFinish={(v) => m.mutate({ note: v.note?.trim() || undefined })}
        >
          <Alert type="info" showIcon message="File attachments ship later" description="For now, note anything you'd like the teacher to see (or a link)." style={{ marginBottom: 12 }} />
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={4} placeholder="Attached below…" />
          </Form.Item>
          {err && <Alert type="error" showIcon message={err} />}
        </Form>
      )}
    </Modal>
  );
}
